/**
 * Syncs June PDP free gift lines with cart state:
 * adds gift at 3+ subscription pouches, removes when below.
 * Works with native cart drawer and UpCart app drawer.
 */
(function () {
  const CONFIG_EL_ID = 'pdp-june-cart-guard-config';
  let isRunning = false;
  let debounceTimer = null;
  let upcartUiTimer = null;
  let upcartMutationTimer = null;
  let isSyncingUpcartUi = false;
  let upcartEventsBound = false;
  const UPCART_GIFT_TILE_CLASS = 'pdp-june-free-gift-tile';
  const UPCART_GIFT_STYLE_ID = 'pdp-june-upcart-gift-style';

  function getConfig() {
    const el = document.getElementById(CONFIG_EL_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  function normalizeProperties(properties) {
    if (!properties) return {};
    if (Array.isArray(properties)) {
      return properties.reduce((acc, entry) => {
        const key = entry?.name || entry?.key;
        if (key) acc[key] = entry.value;
        return acc;
      }, {});
    }
    return properties;
  }

  function getLineProperty(item, key) {
    const props = normalizeProperties(item.properties);
    return props[key] ?? null;
  }

  function getVariantId(item) {
    return item.variant_id ?? item.variantId ?? null;
  }

  function hasSellingPlan(item) {
    return Boolean(
      item.selling_plan_allocation ||
        item.sellingPlanAllocation ||
        item.selling_plan_id ||
        item.sellingPlanId
    );
  }

  function isGiftLine(item, config) {
    if (getLineProperty(item, '_free_gift') === 'true') return true;
    const variantId = getVariantId(item);
    return config.freeGiftVariantId && String(variantId) === String(config.freeGiftVariantId);
  }

  function isSubscribePouchLine(item, config) {
    if (isGiftLine(item, config)) return false;
    if (hasSellingPlan(item)) return true;
    return getLineProperty(item, '_purchase_type') === 'subscribe';
  }

  function inferSticksPerPouch(sticksPerUnit) {
    if (sticksPerUnit === 12) return 12;
    if (sticksPerUnit === 15 || sticksPerUnit === 30) return 15;
    return 15;
  }

  function getLineTierUnits(item) {
    const quantity = item.quantity || 0;
    if (!quantity) return 0;

    const tierUnitsPerItem = getLineProperty(item, '_tier_units_per_item');
    if (tierUnitsPerItem != null && tierUnitsPerItem !== '') {
      const weight = parseInt(String(tierUnitsPerItem), 10);
      if (Number.isFinite(weight) && weight > 0) {
        return quantity * weight;
      }
    }

    const sticksPerUnit = parseInt(String(getLineProperty(item, '_sticks_per_unit')), 10);
    if (Number.isFinite(sticksPerUnit) && sticksPerUnit > 0) {
      const sticksPerPouchProp = parseInt(String(getLineProperty(item, '_sticks_per_pouch')), 10);
      const sticksPerPouch =
        Number.isFinite(sticksPerPouchProp) && sticksPerPouchProp > 0
          ? sticksPerPouchProp
          : inferSticksPerPouch(sticksPerUnit);
      const weight = Math.max(1, Math.floor(sticksPerUnit / sticksPerPouch));
      return quantity * weight;
    }

    return quantity;
  }

  function countSubscribeTierUnits(items, config) {
    return items
      .filter((item) => isSubscribePouchLine(item, config))
      .reduce((sum, item) => sum + getLineTierUnits(item), 0);
  }

  function cartHasGift(cart, config) {
    return Boolean(cart?.items?.some((item) => isGiftLine(item, config)));
  }

  function shouldRemoveGifts(cart, config) {
    if (!cart?.items?.length || !cartHasGift(cart, config)) return false;

    const minTierUnits = config.minSubscribePouches || 3;
    return countSubscribeTierUnits(cart.items, config) < minTierUnits;
  }

  function shouldAddGift(cart, config) {
    if (!config.freeGiftVariantId) return false;
    if (!cart?.items?.length || cartHasGift(cart, config)) return false;

    const minTierUnits = config.minSubscribePouches || 3;
    return countSubscribeTierUnits(cart.items, config) >= minTierUnits;
  }

  function normalizeCartPayload(payload) {
    if (!payload) return null;
    if (payload.items) return payload;
    return null;
  }

  function usesUpcart() {
    return typeof window.upcartRefreshCart === 'function';
  }

  function getUpcartRoot() {
    if (window.upcartDocumentOrShadowRoot) {
      return window.upcartDocumentOrShadowRoot;
    }

    const hostSelectors = ['#upcart-drawer', '[id*="upcart"]', 'upcart-drawer', '.upcart-cart'];
    for (const selector of hostSelectors) {
      const host = document.querySelector(selector);
      if (host?.shadowRoot) return host.shadowRoot;
    }

    return document;
  }

  function ensureUpcartGiftStyles() {
    const roots = new Set([getUpcartRoot(), document]);
    const styleText = `
      .${UPCART_GIFT_TILE_CLASS} .upcart-public-component-product-tile__controls,
      .${UPCART_GIFT_TILE_CLASS} .upcart-public-component-product-tile__quantity-selector,
      .${UPCART_GIFT_TILE_CLASS} .upcart-public-component-product-tile__quantity-minus,
      .${UPCART_GIFT_TILE_CLASS} .upcart-public-component-product-tile__quantity-plus,
      .${UPCART_GIFT_TILE_CLASS} [class*="styles_QuantityField__"],
      [data-pdp-june-free-gift="true"] .upcart-public-component-product-tile__controls,
      [data-pdp-june-free-gift="true"] .upcart-public-component-product-tile__quantity-selector {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;

    roots.forEach((root) => {
      if (!root || root.getElementById?.(UPCART_GIFT_STYLE_ID)) return;

      const style = document.createElement('style');
      style.id = UPCART_GIFT_STYLE_ID;
      style.textContent = styleText;

      if (root === document) {
        document.head.appendChild(style);
      } else if (typeof root.appendChild === 'function') {
        root.appendChild(style);
      }
    });
  }

  function escapeSelectorValue(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(String(value));
    }
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function getCartItemTiles(root) {
    const wrappers = Array.from(root.querySelectorAll('.upcart-public-cart-item'));
    if (wrappers.length) {
      return wrappers.map((wrapper) => wrapper.querySelector('.upcart-public-component-product-tile') || wrapper);
    }

    return Array.from(root.querySelectorAll('.upcart-public-component-product-tile, .upcart-product-item'));
  }

  function findUpcartTileForItem(root, item) {
    const key = item?.key;
    if (!key) return null;

    const byId =
      (typeof root.getElementById === 'function' && root.getElementById(key)) ||
      (typeof document.getElementById === 'function' && document.getElementById(key));

    if (byId) {
      return byId.closest('.upcart-public-component-product-tile, .upcart-public-cart-item, .upcart-product-item') || byId;
    }

    const escapedKey = escapeSelectorValue(key);
    const selectors = [
      `[id="${key.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`,
      `#${escapedKey}`,
      `.upcart-public-cart-item[id="${key.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`,
      `.upcart-public-component-product-tile[id="${key.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`,
      `.upcart-product-item[id="${key.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`,
      `[data-line-key="${key.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`,
    ];

    for (const selector of selectors) {
      try {
        const match = root.querySelector(selector);
        if (!match) continue;
        return match.closest('.upcart-public-component-product-tile, .upcart-public-cart-item, .upcart-product-item') || match;
      } catch (error) {
        // Ignore invalid selectors.
      }
    }

    return null;
  }

  function findUpcartTileByVariantId(root, variantId) {
    if (!variantId) return null;

    const variantIdStr = String(variantId);
    const tiles = getCartItemTiles(root);

    for (const tile of tiles) {
      const variantNode = tile.querySelector('[data-variant-id]');
      if (variantNode && String(variantNode.getAttribute('data-variant-id')) === variantIdStr) {
        return tile;
      }

      if (tile.querySelector(`a[href*="${variantIdStr}"]`)) {
        return tile;
      }

      if (tile.querySelector(`img[src*="${variantIdStr}"]`)) {
        return tile;
      }
    }

    return null;
  }

  function hideGiftControls(tile) {
    if (!tile) return;

    const targets = new Set([tile]);
    const wrapper = tile.closest('.upcart-public-cart-item');
    if (wrapper) targets.add(wrapper);

    targets.forEach((target) => {
      target.classList.add(UPCART_GIFT_TILE_CLASS);
      target.setAttribute('data-pdp-june-free-gift', 'true');

      target
        .querySelectorAll(
          '.upcart-public-component-product-tile__controls, .upcart-public-component-product-tile__quantity-selector, .upcart-public-component-product-tile__quantity-minus, .upcart-public-component-product-tile__quantity-plus, [class*="styles_QuantityField__"], input[type="number"]'
        )
        .forEach((element) => {
          element.setAttribute('hidden', 'hidden');
          element.setAttribute('aria-hidden', 'true');
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.style.setProperty('pointer-events', 'none', 'important');
        });
    });
  }

  function clearGiftControls(tile) {
    if (!tile) return;

    tile.classList.remove(UPCART_GIFT_TILE_CLASS);
    tile.removeAttribute('data-pdp-june-free-gift');
  }

  function syncUpcartGiftTileControls(cart) {
    const config = getConfig();
    if (!config?.freeGiftVariantId || !cart?.items?.length || !usesUpcart() || isSyncingUpcartUi) return;

    isSyncingUpcartUi = true;
    try {
      ensureUpcartGiftStyles();
      const root = getUpcartRoot();
      const tiles = getCartItemTiles(root);
      const useIndexMatch = tiles.length > 0 && tiles.length === cart.items.length;

      tiles.forEach((tile) => clearGiftControls(tile));
      root.querySelectorAll(`[data-pdp-june-free-gift="true"]`).forEach((tile) => {
        clearGiftControls(tile);
      });

      cart.items.forEach((item, index) => {
        if (!isGiftLine(item, config)) return;

        const tile =
          findUpcartTileForItem(root, item) ||
          (useIndexMatch ? tiles[index] : null) ||
          findUpcartTileByVariantId(root, getVariantId(item)) ||
          findUpcartTileByVariantId(root, config.freeGiftVariantId);

        hideGiftControls(tile);
      });
    } finally {
      isSyncingUpcartUi = false;
    }
  }

  function scheduleUpcartGiftUi(cart = null) {
    const run = async () => {
      const nextCart = cart || (await fetchCart());
      if (nextCart) syncUpcartGiftTileControls(nextCart);
    };

    run();
    clearTimeout(upcartUiTimer);
    upcartUiTimer = setTimeout(run, 100);
    setTimeout(run, 250);
    setTimeout(run, 500);
    setTimeout(run, 900);
  }

  function observeUpcartGiftUi() {
    const root = getUpcartRoot();
    if (!root || root.__pdpJuneGiftObserver) return;

    const observer = new MutationObserver(() => {
      if (isSyncingUpcartUi) return;
      clearTimeout(upcartMutationTimer);
      upcartMutationTimer = setTimeout(() => scheduleUpcartGiftUi(), 80);
    });

    observer.observe(root, { childList: true, subtree: true });
    root.__pdpJuneGiftObserver = observer;
  }

  function isValidGiftLine(item) {
    return getLineProperty(item, '_free_gift') === 'true';
  }

  // Collapses every gift line down to a single unit. Guards against manual
  // `/cart/add` workarounds that stack a second gift line (different properties
  // never merge) or bump the quantity above one. The line that was added by the
  // valid gift logic (`_free_gift: 'true'`) is preferred as the survivor.
  async function normalizeGiftLines(config, cart) {
    const giftLines = cart.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => isGiftLine(item, config));

    if (!giftLines.length) return cart;

    const needsNormalize =
      giftLines.length > 1 || giftLines.some(({ item }) => item.quantity > 1);
    if (!needsNormalize) return cart;

    let keepIndex = giftLines.findIndex(({ item }) => isValidGiftLine(item));
    if (keepIndex < 0) keepIndex = 0;
    const keepLine = giftLines[keepIndex].line;

    // Highest line numbers first so earlier lines keep their indexes as
    // zero-quantity lines drop out of the cart.
    const orderedLines = giftLines
      .map(({ line }) => line)
      .sort((a, b) => b - a);

    for (const line of orderedLines) {
      const quantity = line === keepLine ? 1 : 0;
      await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
    }

    return fetchCart();
  }

  async function fetchCart() {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js?ts=${Date.now()}`);
    if (response.ok) {
      return response.json();
    }

    if (typeof window.upcartGetCart === 'function') {
      try {
        const upcartCart = window.upcartGetCart();
        if (upcartCart?.items) return upcartCart;
      } catch (error) {
        console.error(error);
      }
    }

    return null;
  }

  function buildFreeGiftLineItem(config) {
    return {
      id: parseInt(String(config.freeGiftVariantId), 10),
      quantity: 1,
      properties: { _free_gift: 'true', _pdp_june_2026: 'true' },
    };
  }

  async function addMissingGift(config) {
    const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [buildFreeGiftLineItem(config)] }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data?.status) {
      console.warn('Free gift could not be added:', data.description || data.message);
      return null;
    }

    return data;
  }

  async function removeGiftByVariantId(config) {
    const variantId = String(config.freeGiftVariantId);
    const body = { updates: { [variantId]: 0 } };

    if (!usesUpcart()) {
      const cartComponent = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
      if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
        body.sections = cartComponent.getSectionsToRender().map((section) => section.section);
        body.sections_url = window.location.pathname;
      }
    }

    const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) return null;
    return response.json();
  }

  async function removeGiftByLine(cart, config) {
    const giftLines = cart.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => isGiftLine(item, config))
      .map(({ line }) => line)
      .sort((a, b) => b - a);

    let removed = false;
    for (const line of giftLines) {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: 0 }),
      });
      if (response.ok) removed = true;
    }
    return removed;
  }

  async function refreshNativeCartUi(sectionPayload) {
    const cartComponent = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');

    if (sectionPayload?.sections && cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
      cartComponent.getSectionsToRender().forEach((section) => {
        const sectionHtml = sectionPayload.sections[section.section];
        if (!sectionHtml) return;

        const target =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);
        if (!target) return;

        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const inner = parsed.querySelector(section.selector) || parsed.querySelector('.shopify-section');
        if (inner) target.innerHTML = inner.innerHTML;
      });

      const cartDrawerWrapper = document.querySelector('cart-drawer');
      if (cartDrawerWrapper && sectionPayload.item_count != null) {
        cartDrawerWrapper.classList.toggle('is-empty', sectionPayload.item_count === 0);
      }
      return;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return;

    const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id);
    const response = await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`);
    if (!response.ok) return;

    const sections = await response.json();
    try {
      cartDrawer.renderContents({ sections });
    } catch (error) {
      console.error(error);
    }
  }

  async function refreshCartUi(sectionPayload) {
    if (usesUpcart()) {
      window.upcartRefreshCart();
      return;
    }

    await refreshNativeCartUi(sectionPayload);
  }

  async function removeInvalidGifts(config, cart) {
    let sectionPayload = await removeGiftByVariantId(config);
    let nextCart = normalizeCartPayload(sectionPayload) || (await fetchCart());

    if (nextCart && shouldRemoveGifts(nextCart, config)) {
      await removeGiftByLine(nextCart, config);
      nextCart = await fetchCart();
    }

    return nextCart;
  }

  async function syncFreeGift(config) {
    let cart = await fetchCart();
    if (!cart) return false;

    let changed = false;
    const cartBeforeNormalize = cart;
    const normalizedCart = await normalizeGiftLines(config, cart);
    if (normalizedCart && normalizedCart !== cartBeforeNormalize) {
      cart = normalizedCart;
      changed = true;
    }

    let latestCart = cart;

    if (shouldRemoveGifts(cart, config)) {
      latestCart = (await removeInvalidGifts(config, cart)) || cart;
      changed = true;
    } else if (shouldAddGift(cart, config)) {
      const addResult = await addMissingGift(config);
      if (addResult) {
        latestCart = normalizeCartPayload(addResult) || (await fetchCart()) || cart;
        changed = true;
      }
    }

    if (!changed) {
      scheduleUpcartGiftUi(latestCart);
      return false;
    }

    await refreshCartUi(latestCart);
    scheduleUpcartGiftUi(latestCart);

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pdp-june-cart-guard', cartData: latestCart });
    }

    return true;
  }

  async function runGuard() {
    const config = getConfig();
    if (!config?.freeGiftVariantId || isRunning) return;

    isRunning = true;
    try {
      await syncFreeGift(config);
    } catch (error) {
      console.error(error);
    } finally {
      isRunning = false;
    }
  }

  function scheduleGuard(delayMs = 100) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => runGuard(), delayMs);
  }

  function bindUpcartEvents() {
    if (upcartEventsBound) return true;

    const hasSubscriber =
      typeof window.upcartSubscribeCartUpdated === 'function' ||
      typeof window.upcartSubscribeItemRemoved === 'function' ||
      typeof window.upcartSubscribeAddedToCart === 'function' ||
      typeof window.upcartSubscribeCartLoaded === 'function';

    if (!hasSubscriber) return false;

    if (typeof window.upcartSubscribeCartUpdated === 'function') {
      window.upcartSubscribeCartUpdated((event) => {
        scheduleGuard(150);
        scheduleUpcartGiftUi(event?.cart || null);
      });
    }

    if (typeof window.upcartSubscribeItemRemoved === 'function') {
      window.upcartSubscribeItemRemoved(() => {
        scheduleGuard(150);
        scheduleUpcartGiftUi();
      });
    }

    if (typeof window.upcartSubscribeAddedToCart === 'function') {
      window.upcartSubscribeAddedToCart(() => {
        scheduleGuard(150);
        scheduleUpcartGiftUi();
      });
    }

    if (typeof window.upcartSubscribeCartLoaded === 'function') {
      window.upcartSubscribeCartLoaded((event) => {
        scheduleGuard(50);
        scheduleUpcartGiftUi(event?.cart || null);
      });
    }

    upcartEventsBound = true;
    observeUpcartGiftUi();
    scheduleUpcartGiftUi();
    return true;
  }

  function waitForUpcart(maxAttempts = 60) {
    if (bindUpcartEvents()) return;

    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      if (bindUpcartEvents()) {
        clearInterval(timer);
        return;
      }

      if (usesUpcart()) {
        observeUpcartGiftUi();
        scheduleUpcartGiftUi();
      }

      if (attempts >= maxAttempts) {
        clearInterval(timer);
      }
    }, 250);
  }

  function init() {
    if (!getConfig()) return;

    window.pdpJuneCartGuardRun = runGuard;

    scheduleGuard(0);
    waitForUpcart();
    observeUpcartGiftUi();
    scheduleUpcartGiftUi();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'pdp-june-cart-guard') return;
        scheduleGuard(100);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
